import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type {
  Invoice, InvoiceSummary, InvoiceDraft, CreditNoteCreate, ManualPayment,
  InvoiceDetails, InvoiceDetailsUpsert, Checkout,
} from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export type InvoiceFilter = 'all' | 'draft' | 'unpaid' | 'overdue' | 'paid';

export const invoiceKeys = {
  all: (show: InvoiceFilter, tenantId: string) => ['invoices', 'all', show, tenantId] as const,
  one: (id: string) => ['invoices', 'one', id] as const,
  byNumber: (number: string) => ['invoices', 'number', number] as const,
  tenant: (tenantId: string) => ['invoices', 'tenant', tenantId] as const,
  tenantNumber: (tenantId: string, number: string) => ['invoices', 'tenant', tenantId, number] as const,
  details: (tenantId: string) => ['invoice-details', tenantId] as const,
};

/** Every list and invoice, and the payments a recorded payment adds to. */
const refresh = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: ['invoices'] });
  void qc.invalidateQueries({ queryKey: ['payments'] });
};

// ------------------------------------------------------- platform staff

export const useAllInvoices = (show: InvoiceFilter, tenantId?: string, enabled = true) =>
  useQuery({
    queryKey: invoiceKeys.all(show, tenantId ?? ''),
    queryFn: () => api.get<ListResponse<InvoiceSummary>>(
      `/v1/billing/invoices?show=${show}${tenantId ? `&tenant_id=${tenantId}` : ''}`),
    enabled,
  });

export const useInvoice = (id: string | null) =>
  useQuery({
    queryKey: invoiceKeys.one(id ?? ''),
    queryFn: () => api.get<Invoice>(`/v1/billing/invoices/${id}`),
    enabled: !!id,
    retry: false,
  });

/** An issued invoice by its number, however far back. */
export const useInvoiceByNumber = (number: string | null) =>
  useQuery({
    queryKey: invoiceKeys.byNumber(number ?? ''),
    queryFn: () => api.get<Invoice>(`/v1/billing/invoices/number/${encodeURIComponent(number ?? '')}`),
    enabled: !!number,
    retry: false,
  });

/** Creates a draft, or replaces one whole. */
export function useSaveDraft(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvoiceDraft) => (id
      ? api.put<Invoice>(`/v1/billing/invoices/${id}`, body)
      : api.post<Invoice>('/v1/billing/invoices', body)),
    onSuccess: (saved) => { qc.setQueryData(invoiceKeys.one(saved.id), saved); refresh(qc); },
  });
}

export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/billing/invoices/${id}`),
    onSuccess: () => refresh(qc),
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/v1/billing/invoices/${id}/actions/issue`),
    onSuccess: (issued) => { qc.setQueryData(invoiceKeys.one(issued.id), issued); refresh(qc); },
  });
}

export function useCreditNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreditNoteCreate) => api.post<Invoice>(`/v1/billing/invoices/${id}/credit-notes`, body),
    onSuccess: () => refresh(qc),
  });
}

export interface RecordedPayment { receipt_number: string; amount: string; invoices: string[] }

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManualPayment) => api.post<RecordedPayment>('/v1/billing/payments', body),
    onSuccess: () => refresh(qc),
  });
}

// ------------------------------------------------------- a client's own

export const useTenantInvoices = (tenantId: string | null) =>
  useQuery({
    queryKey: invoiceKeys.tenant(tenantId ?? ''),
    queryFn: () => api.get<ListResponse<InvoiceSummary>>(`/v1/tenants/${tenantId}/billing/invoices`),
    enabled: !!tenantId,
  });

export const useTenantInvoice = (tenantId: string | null, number: string | null) =>
  useQuery({
    queryKey: invoiceKeys.tenantNumber(tenantId ?? '', number ?? ''),
    queryFn: () => api.get<Invoice>(
      `/v1/tenants/${tenantId}/billing/invoices/${encodeURIComponent(number ?? '')}`),
    enabled: !!tenantId && !!number,
    retry: false, // a number that is not an invoice is a 404, and a real answer
  });

/** A signed PayFast checkout for what is due on an invoice. */
export function usePayInvoice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (number: string) =>
      api.post<Checkout>(`/v1/tenants/${tenantId}/billing/invoices/${encodeURIComponent(number)}/payments`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['payments'] }); },
  });
}

export const useInvoiceDetails = (tenantId: string | null) =>
  useQuery({
    queryKey: invoiceKeys.details(tenantId ?? ''),
    queryFn: () => api.get<InvoiceDetails>(`/v1/tenants/${tenantId}/billing/invoice-details`),
    enabled: !!tenantId,
  });

export function useSaveInvoiceDetails(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvoiceDetailsUpsert) =>
      api.put<InvoiceDetails>(`/v1/tenants/${tenantId}/billing/invoice-details`, body),
    onSuccess: (saved) => { qc.setQueryData(invoiceKeys.details(tenantId), saved); },
  });
}
